import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * Cart PO Number web component.
 *
 * Persists the PO number as a cart attribute (`Invoice PO Number`) on input so
 * that accelerated checkout buttons (Shop Pay, etc.) also include it, in
 * addition to the standard cart-form submit path used by the Checkout button.
 *
 * Mirrors the cart-note component pattern.
 */
class CartPoNumber extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * Handles updates to the PO number field.
   * @param {InputEvent} event - The input event from the PO number input.
   */
  updatePoNumber = debounce(async (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const poNumber = event.target.value;

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            'Invoice PO Number': poNumber,
          },
        }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
      // Aborts and network blips are non-fatal: the cart-form submit on
      // checkout will still send the value as a fallback.
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('po-number-update:user-action', event);
    }
  }, 250);
}

if (!customElements.get('cart-po-number')) {
  customElements.define('cart-po-number', CartPoNumber);
}
